import { FunctionComponent, ReactNode, useEffect, useMemo } from 'react'

import { AuthContext } from './context/useAuthContext'
import { useAuthState } from './hooks/useAuthState'
import { useUrlToken } from './hooks/useUrlToken'
import { AuthErrorEnum } from './types'
import { getStoredToken, isTokenExpired, isTokenNeedsRefresh } from './utils'

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: FunctionComponent<AuthProviderProps> = ({
  children,
}) => {
  // Get token from URL or localStorage
  const urlToken = useUrlToken()
  const storedToken = getStoredToken()
  const initialToken = urlToken || storedToken

  const { auth, logout, verifyCurrentToken, refreshCurrentToken } =
    useAuthState(initialToken)

  useEffect(() => {
    // Verify token if there is one but not authenticated yet
    if (auth.jwt && !auth.isAuthenticated && !auth.isLoading) {
      verifyCurrentToken()
    }

    if (auth.jwt && isTokenNeedsRefresh(auth.jwt)) {
      refreshCurrentToken()
    }

    // Handle expired tokens
    if (auth.jwt && isTokenExpired(auth.jwt)) {
      logout(AuthErrorEnum.EXPIRED)
    }
  }, [
    initialToken,
    auth.jwt,
    auth.isAuthenticated,
    auth.isLoading,
    verifyCurrentToken,
    logout,
    refreshCurrentToken,
  ])

  // Build context value
  const contextValue = useMemo(
    () => ({
      ...auth,
      logout,
      verifyCurrentToken,
      refreshCurrentToken,
    }),
    [auth, logout, verifyCurrentToken, refreshCurrentToken]
  )

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  )
}
